import { KEY_PATTERNS, VALUE_PATTERNS, VALUE_PARSERS } from 'constants'

/**
 * Clamps a number between a minimum and maximum value.
 *
 * @param {number} num - The number to clamp.
 * @param {number} min - The minimum value.
 * @param {number} max - The maximum value.
 * @returns {number} The clamped number.
 */
export function clamp(num, min, max) {
  return Math.min(Math.max(num, min), max)
}

/**
 * Calls a function when the document's DOM content has loaded' (or immediately if DOM content load is complete).
 *
 * @param {function} callback - The function to call.
 */
export function callOnReady(callback) {
  if (['complete', 'loaded'].includes(document.readyState)) {
    callback()
  } else {
    document.addEventListener('DOMContentLoaded', callback)
  }
}

/**
 * Calls a function when the document is loaded (or immediately if document load is complete).
 *
 * @param {function} callback - The function to call.
 */
export function callOnLoad(callback) {
  if (document.readyState === 'complete') {
    callback()
  } else {
    window.addEventListener('load', callback)
  }
}

/**
 * Delay the execution of a function until after a specified period of inactivity.
 *
 * @param {function} fn - The function to debounce.
 * @param {number} wait - The number of milliseconds to delay.
 * @returns {function} A debounced function.
 */
export function debounce(fn, wait) {
  let timeout
  return (...args) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => fn.apply(this, args), wait)
  }
}

/**
 * Limit the execution of a function to once per specified time interval.
 *
 * @param {function} fn - The function to throttle.
 * @param {number} delay - The number of milliseconds to delay.
 * @returns {function} A throttled function.
 */
export function throttle(fn, delay) {
  let wait = false
  return (...args) => {
    if (wait) return

    fn(...args)
    wait = true

    setTimeout(() => {
      wait = false
    }, delay)
  }
}

/**
 * Creates a function that invokes the original function at most once. Subsequent calls to the returned function return the result of the original function the first time it is invoked.
 *
 * @param {function} fn - The function to memoize.
 * @returns {function} A memoized function.
 */
export function once(fn) {
  let ran = false
  let result
  return function (...args) {
    if (ran) return result
    result = fn.apply(this, args)
    ran = true
    return result
  }
}

/**
 * Creates a function that caches the result of the original function for each distinct set of arguments. Subsequent calls to the memoized function with the same arguments return the cached result.
 *
 * @param {function} fn - The function to memoize.
 * @returns {function} A memoized function.
 */
export function memoize(fn) {
  const cache = new Map()
  return function (...args) {
    const key = JSON.stringify(args)
    if (cache.has(key)) return cache.get(key)

    const result = fn.apply(this, args)
    cache.set(key, result)
    return result
  }
}

/**
 * Creates a curried function that invokes the original function with the arguments it is initially called with, and returns a new function that can be invoked with additional arguments.
 *
 * @param {function} fn - The function to curry.
 * @param {boolean} [reverse=false] - Pass arguments from right to left.
 * @param {number} [arity=fn.length] - The arity of the curried function.
 * @returns {function} A curried function.
 */
export function curry(fn, { reverse = false } = {}, arity = fn.length) {
  return function curried(...args) {
    if (args.length >= arity) {
      if (reverse) args.reverse()
      return fn(...args)
    }
    return (...moreArgs) => curried(...args, ...moreArgs)
  }
}

/**
 * Creates a function that invokes the original function with the arguments it is initially called with, and returns a new function that can be invoked with additional arguments.
 *
 * @param {function} fn - The function to partially apply.
 * @param {...*} args - The arguments to partially apply.
 * @returns {function} A partially applied function.
 */
export function partial(fn, ...args) {
  return (..._args) => fn(...args, ..._args)
}

/**
 * Creates a function that invokes each of the functions in `fns` in turn, passing the return value of one function as the argument to the next.
 *
 * @param {...function} fns - The functions to pipe.
 * @returns {function} A piped function.
 */
export function pipe(...fns) {
  return function piped(...args) {
    // biome-ignore lint/performance/noAccumulatingSpread: piping arguments
    return fns.reduce((result, fn) => [fn.call(this, ...result)], args)[0]
  }
}

/**
 * Creates a function that invokes each of the functions in `fns` in reverse order, passing the return value of one function as the argument to the next.
 *
 * @param {...function} fns - The functions to compose.
 * @returns {function} A composed function.
 */
export function compose(...fns) {
  return function composed(...args) {
    // biome-ignore lint/performance/noAccumulatingSpread: composing arguments
    return fns.reduceRight((result, fn) => [fn.call(this, ...result)], args)[0]
  }
}

/**
 * Creates a new object from specified keys of an object.
 *
 * @param {Object} obj - The object to pick from.
 * @param {...string} keys - The keys to pick.
 * @returns {Object} An object composed of the original object with only the picked keys.
 */
export function pick(obj, keys) {
  return keys.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      acc[key] = obj[key]
    }
    return acc
  }, {})
}

/**
 * Creates a new object from an object with specified keys omitted.
 *
 * @param {Object} obj - The object to omit from.
 * @param {...string} keys - The keys to omit.
 * @returns {Object} An object composed of the original object without the omitted keys.
 */
export function omit(obj, keys) {
  return Object.keys(obj)
    .filter(key => !keys.includes(key))
    .reduce((acc, key) => {
      acc[key] = obj[key]
      return acc
    }, {})
}

/**
 * Creates an array of tuples from the elements of 2 arrays.
 *
 * @param {...Array} arrays - The arrays to zip.
 * @returns {Array} An array of tuples based on the 2 input arrays.
 */
export function zip(...arrays) {
  const maxLength = Math.max(...arrays.map(array => array.length))
  return Array.from({ length: maxLength }).map((_, i) => {
    return Array.from({ length: arrays.length }, (_, j) => arrays[j][i])
  })
}

/**
 * Creates an object from an array of tuples created using the elements of 2 arrays.
 *
 * @param {...Array} arrays - The arrays to zip and use for object entries.
 * @returns {Object} An object created from an array of tuples produced from zipping the 2 input arrays.
 */
export function objectZip(...arrays) {
  return Object.fromEntries(zip(...arrays))
}

/**
 * Creates helper functions for performing precise decimal math operations while avoiding floating point precision issues.
 *
 * @param {number} [precision=2] - Number of decimal places to maintain precision for
 * @returns {Object} Object containing prepare and finalize functions
 * @returns {Function} prepare - Converts decimal number(s) to integer(s) by multiplying by 10^precision
 * @returns {Function} finalize - Converts integer back to decimal by dividing by 10^precision
 *
 * @example
 * const { prepare, finalize } = fractionalMath(3);
 * const num1 = prepare(1.234); // 1234
 * const num2 = prepare(5.678); // 5678
 * const sum = num1 + num2; // 6912
 * const result = finalize(sum); // 6.912
 */
export function fractionalMath(precision = 2) {
  const multiplier = 10 ** precision
  const prepare = (...input) => {
    const prepared = input.map(value => Number.parseInt(Math.round(value * multiplier), 10))
    return prepared.length === 1 ? prepared[0] : prepared
  }
  const finalize = output => output / multiplier

  return { prepare, finalize }
}

/**
 * Creates an array of numbers from a specified range.
 *
 * @param {number} [_min=0] - The minimum value of the range.
 * @param {number} [_max] - The maximum value of the range. If not specified, the value of `min` is used.
 * @param {number} [_step=1] - The interval between each number in the range.
 * @returns {Array} An array of numbers.
 */
export function range(_min, _max, _step = 1) {
  let min = _min
  let max = _max
  let step = _step

  if (max === undefined) {
    max = min
    min = step
  }

  min = Number(min)
  max = Number(max)
  step = Number(step)

  return Array.from({ length: Math.floor((max - min) / step) + 1 }, (_, i) => min + i * step)
}

/**
 * Re-injects all script tags within an AJAX-loaded element to allow script execution. (By default, script execution is disabled when using element.innerHTML).
 *
 * @param {Element} element - The element with inline script tags.
 */
export function reinjectScripts(element) {
  console.debug('Reinjecting script tags for element:', element)

  for (const oldScriptTag of element.querySelectorAll('script')) {
    const newScriptTag = document.createElement('script')

    for (const attribute of oldScriptTag.attributes) {
      newScriptTag.setAttribute(attribute.name, attribute.value)
    }

    newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML))
    oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag)
  }
}

/**
 * Creates an array of numbers with a fixed number of evenly spaced values between min and max.
 *
 * @param {number} _min - The minimum value of the range.
 * @param {number} _max - The maximum value of the range.
 * @param {number} _count - The number of values to generate.
 * @returns {Array} An array of evenly spaced numbers between min and max.
 */
export function fixedRange(_min, _max, _count) {
  let min = _min
  let max = _max
  let count = _count

  min = Number(min)
  max = Number(max)
  count = Number(count)

  const diff = max - min
  const step = diff / (count - 1)
  return range(min, max, step)
}

/**
 * Creates an array of numbers with a fixed number of evenly spaced values between a modified range.
 * The range is calculated by taking a base value and a modified value, finding their difference,
 * and using that to create a min/max range that is scaled by a multiplier.
 *
 * @param {number} _base - The base value to calculate the range from
 * @param {number} _modified - The modified value to calculate the difference from
 * @param {number} [count=3] - The number of evenly spaced values to generate
 * @param {number} [mult=2] - The multiplier used to scale the min/max range
 * @param {number} [inputPrecision=3] - The decimal precision to use for calculations
 * @returns {Array<number>} An array of evenly spaced numbers between the calculated min and max values
 */
export function modRange(_base, _modified, count = 3, mult = 2, inputPrecision = 3) {
  const { prepare, finalize } = fractionalMath(inputPrecision)
  const [base, modified] = [prepare(_base), prepare(_modified)]

  const diff = modified - base
  const min = base + diff / mult
  const max = base + diff * mult

  const segmentLength = Math.floor((count - 1) / 2) + 1
  return [...fixedRange(min, modified, segmentLength), ...fixedRange(modified, max, segmentLength).slice(1)].map(
    finalize
  )
}

/**
 * Converts a camelCase string to another case.
 *
 * @param {string} str - The string to convert.
 * @returns {string} The converted string.
 */
export function fromCamel(str, separator = '-') {
  return str === str.toLowerCase() ? str : str.replace(/[A-Z]/g, m => `${separator}${m.toLowerCase()}`)
}

/**
 * Converts a string to camelCase.
 *
 * @param {string} str - The string to convert.
 * @returns {string} The converted string.
 */
export function toCamel(str) {
  if (/^[a-zA-Z]+$/.test(str)) return str

  return str.toLowerCase().replace(/[-_\s]./g, m => m[1].toUpperCase())
}

/**
 * Converts a string to PascalCase.
 *
 * @param {string} str - The string to convert.
 * @returns {string} The converted string.
 */
export function toPascal(str) {
  return capitalize(toCamel(str))
}

export const PROP_AXIS_MAP = {
  top: 'y',
  right: 'x',
  bottom: 'y',
  left: 'x',
  width: 'x',
  height: 'y',
  block: 'y',
  inline: 'x',
  column: 'x',
  row: 'y',
}
export const parseAxis = keyword => PROP_AXIS_MAP[keyword] ?? keyword

export function attribute(element, key, { boolean = false } = {}) {
  if (!element) {
    throw new Error('Failed to get attribute value. No element provided')
  }

  if (!key) {
    const _boolean = boolean
    return (key, { boolean = _boolean } = {}) => attribute(element, key, { boolean })
  }

  if (boolean && !element.hasAttribute(key)) return false

  return parseAttribute(element.getAttribute(key), key)
}

/**
 * Parses an attribute value into a usable value.
 *
 * @param {string} value - The attribute value.
 * @param {string|null} key - The attribute key.
 * @returns {boolean|number|Array|null} The parsed value.
 */
export function parseAttribute(value, key) {
  if (typeof value === 'undefined' || value === 'null') return null
  if (value === '') return true

  let parsedValue = value

  for (const VALUE_TYPE of Object.keys(VALUE_PATTERNS)) {
    if (VALUE_PATTERNS[VALUE_TYPE].test(parsedValue)) {
      parsedValue = VALUE_PARSERS[VALUE_TYPE](parsedValue)
    }
  }

  if (key) {
    for (const KEY_TYPE of Object.keys(KEY_PATTERNS)) {
      if (KEY_PATTERNS[KEY_TYPE].test(key)) {
        parsedValue = VALUE_PARSERS[KEY_TYPE](parsedValue)
      }
    }
  }

  if (isBase64(parsedValue)) {
    parsedValue = parseBase64Object(parsedValue)
  }

  return parsedValue
}

/**
 * Parses a boolean attribute value into a usable value.
 *
 * @param {string} value - The boolean attribute value.
 * @returns {boolean|number|Array|null} The parsed value.
 */
export function parseBooleanAttribute(value) {
  return value !== null && value !== 'false'
}

export const NOT_BASE64_PATTERN = /[^A-Z0-9+\/=]/i
export const URLSAFE_BASE64_PATTERN = /^[A-Z0-9_\-]*$/i
/**
 * Parses a string to determine if it is a valid Base64 string.
 *
 * @param {string} value - The string to parse.
 * @param {boolean} [urlSafe=false] - A flag indicating whether the input string is URL-safe Base64.
 * @returns {boolean} - Returns `true` if the input string is a valid Base64 string, otherwise `false`.
 *
 * @example
 * const isValidBase64 = isBase64('SGVsbG8gV29ybGQh'); // true
 * const isNotValidBase64 = isBase64('InvalidBase64String'); // false
 */
export const isBase64 = (value, urlSafe = false) => {
  if (!value) return false

  if (urlSafe) return URLSAFE_BASE64_PATTERN.test(value)

  const length = value.length

  if (length % 4 !== 0 || NOT_BASE64_PATTERN.test(value)) return false

  const firstPaddingChar = value.indexOf('=')
  return (
    firstPaddingChar === -1 ||
    firstPaddingChar === length - 1 ||
    (firstPaddingChar === length - 2 && value[length - 1] === '=')
  )
}

/**
 * Takes a string and attempts to decode it as Base64 and parse it as JSON.
 * Returns the parsed JSON if successful, otherwise returns the original string.
 *
 * @param {string} rawValue - The Base64-encoded string to parse.
 * @returns {string|object} - The parsed JSON object if successful,
 *                            the original string if unsuccessful.
 */
export const parseBase64Object = rawValue => {
  const value = atob(rawValue)
  let parsedValue

  try {
    parsedValue = JSON.parse(value)
  } catch (e) {
    parsedValue = rawValue
  }

  return parsedValue
}

export const roundToChunk = (n, chunks = 1, { method = 'round', precision = 2 } = {}) =>
  (Math[method](n * chunks) / chunks).toFixed(precision)

/**
 * Blends two gsap easing functions together.
 *
 * @param {string} startEase - The starting easing function.
 * @param {string} endEase - The ending easing function.
 * @param {string} blender - The blending easing function.
 * @param {number} startBlendProgress - The starting blend progress.
 * @returns {function} A blended easing function.
 */
export function blendEases(startEase, endEase, blender, startBlendProgress) {
  const s = gsap.parseEase(startEase)
  const e = gsap.parseEase(endEase)
  const p = startBlendProgress || 0

  const blenderFunction = gsap.parseEase(blender || 'power3.inOut')

  return (v) => {
    if (v <= p) return s(v)
    const b = blenderFunction((v - p) / (1 - p))
    return s(v) * (1 - b) + e(v) * b
  }
}

/**
 * Checks if a value is iterable.
 *
 * @param {unknown} value - The value to check.
 * @returns {boolean} `true` if the value is iterable, otherwise `false`.
 */
export function isIterable(value) {
  return value != null && value !== undefined && typeof value[Symbol.iterator] === 'function'
}

/**
 * Capitalizes the first character of a string.
 *
 * @param {string} string The string to capitalize.
 * @returns {string} The capitalized string.
 */
export const capitalize = string => string.charAt(0).toUpperCase() + string.slice(1)

/**
 * Pauses execution for a specified number of milliseconds.
 *
 * @param {number} timeout The number of milliseconds to pause.
 * @returns {Promise<void>} A promise that resolves after the specified number of
 * milliseconds.
 */
export const sleep = timeout => {
  return new Promise(resolve => setTimeout(resolve, timeout))
}

/**
 * Checks if two arrays have at least one common element.
 *
 * @param {Array} arr1 - The first array.
 * @param {Array} arr2 - The second array.
 * @returns {boolean} - Returns true if there is at least one common element, otherwise false.
 * @example
 * const arr1 = [1, 2, 3];
 * const arr2 = [4, 5, 6];
 * console.log(arraysIntersect(arr1, arr2)); // false
 *
 * const arr3 = [1, 2, 3];
 * const arr4 = [2, 3, 4];
 * console.log(arraysIntersect(arr3, arr4)); // true
 */
export const arraysIntersect = (arr1, arr2) => arr1.some(item => arr2.includes(item))

/**
 * Returns an array containing elements that exist in both input arrays.
 *
 * @param {Array} arr1 - The first array to compare.
 * @param {Array} arr2 - The second array to compare.
 * @returns {Array} - An array containing all elements that exist in both input arrays.
 * @example
 * const arr1 = [1, 2, 3];
 * const arr2 = [2, 3, 4];
 * console.log(arrayIntersections(arr1, arr2)); // [2, 3]
 */
export const arrayIntersections = (arr1, arr2) => arr1.filter(item => arr2.includes(item))

/**
 * Returns an array containing elements that exist in one array but not the other.
 * When bidirectional is true, returns elements unique to either array.
 *
 * @param {Array} arr1 - The first array to compare
 * @param {Array} arr2 - The second array to compare
 * @param {Object} [options] - Optional configuration object
 * @param {boolean} [options.bidirectional=false] - If true, returns elements unique to either array. If false, only returns elements unique to arr1.
 * @returns {Array} Array of elements that exist in one array but not the other
 * @example
 * const arr1 = [1, 2, 3];
 * const arr2 = [2, 3, 4];
 * console.log(arrayDifferences(arr1, arr2)); // [1]
 * console.log(arrayDifferences(arr1, arr2, { bidirectional: true })); // [1, 4]
 */
export const arrayDifferences = (arr1, arr2, { bidirectional = false } = {}) => {
  const diff1 = arr1.filter(item => !arr2.includes(item))
  if (!bidirectional) return diff1

  const diff2 = arr2.filter(item => !arr1.includes(item))
  return [diff1, diff2]
}

export const defineElement = (element, source) => {
  if (!element) {
    console.error('Failed to define element. No tag name provided.')
    return
  }

  if (!source) {
    console.error('Failed to define element. No source class provided.')
    return
  }

  if (customElements.get(element)) return

  customElements.define(element, source)
}

export const getElement = (element) => {
  if (customElements.get(element)) {
    return customElements.get(element)
  }

  return null
}

/**
 * Converts a string to a sanitized kebab-case string. It does the same thing as Shopify's `handle` filter.
 *
 * @param {string} str - The input string to transform.
 * @returns {string} The transformed string.
 */
export const handle = str =>
  str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-$/, '')
    .replace(/^-/, '')

/**
 * Generate all possible combinations of elements, using one value from each input array.
 *
 * @param {...Array} arrays - Any number of input arrays
 * @returns {Array} An array of arrays containing all possible combinations
 */
export const generateCombinations = (...arrays) => {
  if (arrays.length === 0) return [[]]

  const [first, ...rest] = arrays
  const combsWithoutFirst = generateCombinations(...rest)

  return first.flatMap(item => combsWithoutFirst.map(comb => [item, ...comb]))
}

/**
 * Returns a Promise that resolves when all animations on an element have completed.
 *
 * @param {HTMLElement} element - The element whose animations to wait for
 * @returns {Promise} A Promise that resolves when all animations have finished
 */
export const animationsComplete = element => Promise.allSettled(element.getAnimations().map(animation => animation.finished))

/**
 * Updates a URL parameter in the current page URL without causing a page reload.
 *
 * @param {string} key - The parameter key to update
 * @param {string|number|boolean|null} value - The value to set. If null or undefined, the parameter will be removed
 * @returns {void}
 * @example
 * // Add or update a parameter
 * updateUrlParam('sort', 'price');
 * // Remove a parameter
 * updateUrlParam('sort', null);
 */
export const updateUrlParam = (key, value) => {
  const url = new URL(window.location.href)
  if (value === null || value === undefined) {
    url.searchParams.delete(key)
  } else {
    url.searchParams.set(key, value)
  }
  window.history.replaceState({ path: url.toString() }, '', url.toString())
}

export const escapeRegex = (string) => {
  if (typeof string !== 'string') {
    throw new TypeError('Expected a string')
  }

  // Escape characters with special meaning either inside or outside character sets.
  // Use a simple backslash escape when it’s always valid, and a `\xnn` escape when the simpler form would be disallowed by Unicode patterns’ stricter grammar.
  return string.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&').replace(/-/g, '\\x2d')
}

/**
 * Creates a Promise and cleanup function from a callback-based function.
 *
 * @param {function} fn - A function that takes a callback as its argument and returns a cleanup function
 * @returns {[Promise, function]} A tuple containing the Promise and cleanup function
 * @example
 * const [promise, cleanup] = promisify(callback => {
 *   const timeoutId = setTimeout(callback, 1000);
 *   return () => clearTimeout(timeoutId);
 * });
 */
export const promisify = fn => {
  let cleanupFn
  return [
    new Promise(resolve => {
      cleanupFn = fn(() => resolve(null))
    }),
    cleanupFn,
  ]
}

/**
 * Takes an array of promisified functions and races their promises, ensuring all cleanup functions are called.
 *
 * @param {Array<[Promise, function]>} promisifiedFunctions - Array of tuples containing a Promise and its cleanup function
 * @returns {Promise} A Promise that resolves with the first resolved Promise's value
 * @example
 * const [promise1, cleanup1] = promisify(fn1);
 * const [promise2, cleanup2] = promisify(fn2);
 * promisifyRace([[promise1, cleanup1], [promise2, cleanup2]])
 *   .then(result => console.log('First to resolve:', result));
 */
export const promisifyRace = promisifiedFunctions => {
  const promises = promisifiedFunctions.map(([promise]) => promise)
  const cleanups = promisifiedFunctions.map(([, cleanup]) => cleanup)
  return Promise.race(promises).finally(() => {
    for (const cleanup of cleanups) {
      cleanup()
    }
  })
}

// TODO: Update to support filters etc. for child transitions and the like
// TODO: -> Disconnect only on filter success
/**
 * Waits for a CSS transition to end or times out after a specified duration.
 * 
 * @param {HTMLElement} element - The element to observe for transition end.
 * @param {number} [timeout=1000] - Maximum time to wait in milliseconds before timing out.
 * @returns {Promise<null>} A promise that resolves when the transition ends or times out.
 * @example
 * // Wait for a transition to end or timeout after 2 seconds
 * onTransitionEnd(element, 2000).then(() => {
 *   console.log('Transition ended or timed out');
 * });
 */
export const onTransitionEnd = (element, timeout = 1000) => {
  const addTransitionEndListener = element => fn => {
    element.addEventListener('transitionend', fn)
    return () => element.removeEventListener('transitionend', fn)
  }

  const addTimeOut = duration => fn => {
    const timeoutID = setTimeout(fn, duration)
    return () => clearTimeout(timeoutID)
  }

  const transitionPromise = promisify(addTransitionEndListener(element))
  const timeoutPromise = promisify(addTimeOut(timeout))
  return promisifyRace([transitionPromise, timeoutPromise])
}

/**
 * Check if a deeply nested property exists in an object.
 * @param {Array} props - The path to the property.
 * @param {Object} [obj=window] - The object to check.
 * @returns {boolean} - True if the property exists, false otherwise.
 */
export const checkNested = (props, obj = window) => {
  let target = obj
  for (let i = 0; i < props.length; i++) {
    if (!target || !Object.prototype.hasOwnProperty.call(target, props[i])) {
      return false;
    }
    target = target[props[i]];
  }
  return true;
}


/**
 * Waits for a predicate function to return a truthy value, checking at specified intervals.
 *
 * @param {Function} predicate - An async function that returns a value to check.
 * @param {Object} options - Configuration options.
 * @param {number} [options.timeout] - Maximum time to wait in milliseconds before rejecting.
 * @param {number} [options.interval=100] - Time between checks in milliseconds.
 * @returns {Promise<any>} A promise that resolves with the predicate's result or rejects on timeout.
 * @example
 * // Wait for an element to exist
 * await waitFor(async () => document.querySelector('.my-element'), { timeout: 5000 });
 * // Wait for a condition to be true
 * await waitFor(async () => someValue > 10, { interval: 200 });
 */
export function waitFor(predicate, { timeout, interval = 100 } = {}) {
  return new Promise((resolve, reject) => {
    let running = true

    const check = async () => {
      const res = await predicate()
      if (res) return resolve(res)
      if (running) setTimeout(check, interval)
    }

    check()

    if (!timeout) return
    setTimeout(() => {
      running = false
      reject()
    }, timeout)
  })
}